import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { CcfTypography, useTranslator } from '@nice-ccf/ui-controls';
import { CcfAgentCopilotContainerFunctions } from '../ccf-copilot/ccf-live-copilot-helper';
import { ProcessAutomationRun } from './model/process-automation';
import styles from './ccf-process-automation.module.scss';

export interface CcfProcessAutomationResultProps {
  run: ProcessAutomationRun;
  contact: any;
}

/**
 * Completed task output: the agent-centric summary and the patron-centric
 * message, which the agent can edit, insert into the reply (check) or send.
 * @param props - CcfProcessAutomationResultProps
 * @example <CcfProcessAutomationResult />
 * @returns task result
 */
export function CcfProcessAutomationResult({
  run,
  contact,
}: CcfProcessAutomationResultProps) {
  const [translate] = useTranslator();
  const dispatch = useDispatch();
  const output = run.output;
  const [customerMessage, setCustomerMessage] = useState(
    output?.customerMessage ?? ''
  );
  const [isEditing, setIsEditing] = useState(false);
  const [sentAction, setSentAction] = useState<string>();
  const caseId = String(contact?.id ?? '');

  useEffect(() => {
    setCustomerMessage(output?.customerMessage ?? '');
    setSentAction(undefined);
    setIsEditing(false);
  }, [run.runId]);

  if (!output) return null;

  /**
   * Reuses the Copilot insert / send handler for the reply editor
   * @param action - 'insertWorkflowResult' | 'sendWorkflowResult'
   * @example handleInsertOrSend('sendWorkflowResult')
   */
  const handleInsertOrSend = (action: string) => {
    CcfAgentCopilotContainerFunctions.handleInsertOrSendData(
      customerMessage,
      action,
      caseId,
      dispatch
    );
    setSentAction(action);
    setIsEditing(false);
  };

  const isSent = sentAction === 'sendWorkflowResult';

  return (
    <Box className={styles.result}>
      {output.summary && (
        <CcfTypography className={styles.hintText}>
          {translate('paForAgent')}: {output.summary}
        </CcfTypography>
      )}
      {output.customerMessage !== undefined && (
        <>
          {isEditing ? (
            <TextField
              multiline
              fullWidth
              size="small"
              minRows={2}
              autoFocus
              value={customerMessage}
              onChange={(event) => setCustomerMessage(event.target.value)}
              inputProps={{ 'aria-label': translate('paForCustomer') }}
            />
          ) : (
            <Tooltip title={isSent ? '' : translate('paEditMessage')}>
              <CcfTypography
                className={styles.customerMessage}
                role={isSent ? undefined : 'button'}
                tabIndex={isSent ? -1 : 0}
                onClick={() => !isSent && setIsEditing(true)}
                onKeyDown={(event: any) =>
                  event.key === 'Enter' && !isSent && setIsEditing(true)
                }
              >
                {customerMessage}
              </CcfTypography>
            </Tooltip>
          )}
          <Box
            className={styles.resultActions}
            sx={{ borderTopColor: 'divider' }}
          >
            <Tooltip title={translate('paInsertMessage')}>
              <span>
                <IconButton
                  size="small"
                  color={sentAction ? 'success' : 'default'}
                  aria-label={translate('paInsertMessage')}
                  disabled={!customerMessage.trim() || isSent}
                  onClick={() => handleInsertOrSend('insertWorkflowResult')}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={isSent ? translate('paSent') : translate('paSendToCustomer')}
            >
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={translate('paSendToCustomer')}
                  disabled={!customerMessage.trim() || isSent}
                  onClick={() => handleInsertOrSend('sendWorkflowResult')}
                >
                  <SendOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );
}

export default CcfProcessAutomationResult;
